/**
 * Phase 36 Batch AH — Tri-view Deal Score on the Summary tab.
 *
 * Renders three side-by-side cards (Asking / Counter-offer / Target
 * yield), each carrying its own Deal Score gauge, verdict pill, and
 * headline KPIs (cap rate, cash-on-cash, monthly cash flow). The card
 * whose `basis` matches `deal.effective_purchase_price_basis` is
 * highlighted with an indigo ring + "ACTIVE" chip + `aria-pressed`.
 *
 * Click semantics (locked in with the user — see Batch AH spec):
 *   - ASKING        → PATCH /target-offer { basis: "purchase_price" }
 *                     (restores wizard original; clears Batch AF override)
 *   - COUNTER-OFFER → PATCH /target-offer { basis: "user_target_offer",
 *                                            amount: <counter price> }
 *                     Enabled only when the deal already has a
 *                     user_target_offer set; otherwise the card shows
 *                     a "Set a counter-offer →" deep-link to the
 *                     Negotiation tab.
 *   - TARGET YIELD  → INFORMATIONAL ONLY. No click-to-switch. Shows a
 *                     tooltip explaining the walk-away anchor concept.
 *                     The "target yield" price is solver-derived and
 *                     intentionally NOT part of the
 *                     `effective_purchase_price_basis` enum.
 *
 * After a successful PATCH:
 *   • setDeal(fresh) via the parent's onDealUpdated callback
 *   • posthog.capture("summary_scenario_clicked",
 *                     { deal_id, from_basis, to_basis, scenario })
 *   • Dispatch the legacy `runidea:deal-updated` window event so any
 *     other listener (NegotiationTab pattern) is in sync.
 *
 * Data source: GET /api/deals/{id}/score-scenarios?target_pct=8 — no
 * LLM, no writes, cheap. Refetches when the deal's effective price
 * changes elsewhere on the page.
 *
 * Mobile (<640px): the 3 cards stack vertically. Gauge stays compact.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
// Phase 47.2 — PSF/PSM formatter for the per-tile price subtitle.
import { formatPsfPsm } from "@/lib/priceNormalisation";
import {
  Tag,
  Banknote,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Check,
  Loader2,
  AlertTriangle,
  Info,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";


// `basis` is `null` for target_yield because it is NOT a member of the
// `effective_purchase_price_basis` enum — it's a solver-derived display.
const SCENARIO_DEFS = [
  {
    id: "asking",
    label: "Asking",
    icon: Tag,
    accent: "slate",
    basis: "purchase_price",
    clickable: true,
    emptyHint: "No listing price recorded.",
  },
  {
    id: "counter_offer",
    label: "Counter-offer",
    icon: Banknote,
    accent: "amber",
    basis: "user_target_offer",
    clickable: true,
    emptyHint: "Set a counter-offer first.",
  },
  {
    id: "target_yield",
    label: "Target yield",
    icon: TrendingUp,
    accent: "indigo",
    basis: null,
    clickable: false,
    emptyHint: "Add rent + expenses to solve a target-yield price.",
  },
];


function scoreTone(score) {
  if (score == null) return { ring: "#cbd5e1", text: "text-slate-400" };
  if (score >= 80) return { ring: "#059669", text: "text-emerald-700" };
  if (score >= 65) return { ring: "#4f46e5", text: "text-indigo-700" };
  if (score >= 50) return { ring: "#d97706", text: "text-amber-700" };
  return { ring: "#dc2626", text: "text-rose-700" };
}


function verdictPill(verdict, score) {
  const v = (verdict || "").toLowerCase();
  if (v.startsWith("buy") || v === "strong" || v === "strong buy") {
    return "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200";
  }
  if (v.startsWith("hold") || v === "acceptable") {
    return "bg-amber-100 text-amber-800 ring-1 ring-amber-200";
  }
  if (v.startsWith("pass") || v === "risky" || v === "reject") {
    return "bg-rose-100 text-rose-800 ring-1 ring-rose-200";
  }
  return scoreTone(score).text + " bg-slate-100 ring-1 ring-slate-200";
}


function fmtMoney(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return "$" + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}


function MiniGauge({ score, size = 72 }) {
  const tone = scoreTone(score);
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const value = score == null ? 0 : Math.max(0, Math.min(100, score));
  const offset = circumference * (1 - value / 100);
  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e2e8f0"
          strokeWidth="6"
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={tone.ring}
          strokeWidth="6"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.5s ease, stroke 0.4s ease" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className={`text-xl font-bold tabular-nums ${tone.text}`}>
          {score == null ? "—" : score}
        </span>
      </div>
    </div>
  );
}


export default function DealScoreTriView({ deal, onDealUpdated, targetPct = 8 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [applying, setApplying] = useState(null);  // scenario id in flight
  const activeBasis = deal?.effective_purchase_price_basis || "purchase_price";
  // Phase 44 Batch BF2 — switch tile KPIs + target-yield label to
  // flip semantics ("Max Bid Solver" instead of "Target yield").
  const isFlip = (deal?.deal_type || "").toLowerCase() === "flip";
  // Phase 47 Batch 47B — BRRRR has its own KPI vocabulary (Capital
  // Recycled / Equity Created / Cash Left in Deal). Same render
  // pattern as flip — branch the trio in <ScoreScenarioCard>.
  const isBrrrr = (deal?.deal_type || "").toLowerCase() === "brrrr";

  const fetchScenarios = useCallback(async () => {
    if (!deal?.id) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const { data: payload } = await api.get(
        `/deals/${deal.id}/score-scenarios?target_pct=${targetPct}`
      );
      setData(payload);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e?.message || "Could not load scenarios.");
    } finally {
      setLoading(false);
    }
    // Deal price/basis are listed so the callback identity changes when
    // those props mutate (e.g. NegotiationTab applies a counter-offer)
    // and the downstream useEffect refetches without needing to repeat
    // them in its own dep list. CRA's exhaustive-deps rule flags them as
    // unused inside the body — that's exactly the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deal?.id,
    deal?.purchase_price,
    deal?.user_target_offer,
    deal?.effective_purchase_price_basis,
    targetPct,
  ]);

  // Refetch whenever the deal's effective price, target-offer, or basis
  // changes — so applying a counter-offer elsewhere on the page (e.g.
  // NegotiationTab) propagates back into this card immediately.
  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const cards = useMemo(() => {
    if (!data) return [];
    return SCENARIO_DEFS.map((def) => {
      const payload = data[def.id];
      const isActive = def.basis != null && activeBasis === def.basis;
      return { def, payload, isActive };
    });
  }, [data, activeBasis]);

  // Phase 36 Batch AL — fire `counter_offer_ai_suggestion_shown` once
  // per render of an AI-suggested counter card. Keyed on the suggested
  // price so refetches with the same number don't double-fire, but a
  // genuine re-solve does.
  const [lastAiShown, setLastAiShown] = useState(null);
  useEffect(() => {
    const co = data?.counter_offer;
    if (!co || co.source !== "ai_suggested" || !co.price) return;
    const key = `${deal?.id}:${co.price}`;
    if (key === lastAiShown) return;
    setLastAiShown(key);
    try {
      window.posthog?.capture?.("counter_offer_ai_suggestion_shown", {
        deal_id: deal?.id,
        suggested_price: co.price,
        reason: co.suggested_reason || null,
        target_coc_pct: co.target_coc_pct || null,
      });
    } catch { /* posthog blocked — ignore */ }
  }, [data?.counter_offer, deal?.id, lastAiShown]);

  const handleApply = async (scenario) => {
    if (!scenario.def.clickable) return;
    if (!scenario.payload || !scenario.payload.price) return;
    if (scenario.isActive) return;
    setApplying(scenario.def.id);
    try {
      const body = scenario.def.basis === "purchase_price"
        ? { basis: "purchase_price" }
        : { basis: scenario.def.basis, amount: scenario.payload.price };
      const { data: fresh } = await api.patch(
        `/deals/${deal.id}/target-offer`,
        body,
      );
      onDealUpdated?.(fresh);
      try {
        window.dispatchEvent(
          new CustomEvent("runidea:deal-updated", { detail: fresh })
        );
      } catch { /* ignore */ }
      try {
        // Phase 36 Batch AL — if this was an AI-suggested counter, also
        // fire the funnel event so we can measure how often investors
        // accept the AI's offered price vs typing their own.
        if (scenario.payload?.source === "ai_suggested") {
          window.posthog?.capture?.("counter_offer_ai_suggestion_applied", {
            deal_id: deal.id,
            suggested_price: scenario.payload.price,
            from_basis: activeBasis,
            reason: scenario.payload?.suggested_reason || null,
          });
        }
        window.posthog?.capture?.("summary_scenario_clicked", {
          deal_id: deal.id,
          scenario: scenario.def.id,
          from_basis: activeBasis,
          to_basis: scenario.def.basis,
          new_price: scenario.payload.price,
          source: scenario.payload?.source || "user_set",
        });
      } catch { /* posthog blocked — ignore */ }
      toast.success(
        scenario.def.id === "asking"
          ? "Restored to original asking price"
          : `Counter-offer ${fmtMoney(scenario.payload.price)} applied`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply scenario.");
    } finally {
      setApplying(null);
    }
  };

  if (loading && !data) {
    return (
      <section
        data-testid="deal-score-triview-loading"
        className="grid gap-3 sm:grid-cols-3"
      >
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-44 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </section>
    );
  }

  if (errorMsg) {
    return (
      <section
        data-testid="deal-score-triview-error"
        className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium">Could not load scenarios</p>
          <p className="mt-0.5 text-xs">{errorMsg}</p>
        </div>
      </section>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <section
        data-testid="deal-score-triview"
        aria-label="Deal score across pricing scenarios"
        className="grid gap-3 sm:grid-cols-3"
      >
        {cards.map((c) => (
          <ScoreScenarioCard
            key={c.def.id}
            card={c}
            targetPct={targetPct}
            applying={applying === c.def.id}
            onApply={() => handleApply(c)}
            isFlip={isFlip}
            isBrrrr={isBrrrr}
            sqft={deal?.property?.square_feet}
          />
        ))}
      </section>
    </TooltipProvider>
  );
}


function ScoreScenarioCard({ card, applying, onApply, targetPct, isFlip = false, isBrrrr = false, sqft = null }) {
  const { def, payload, isActive } = card;
  const Icon = def.icon;
  const score = payload?.deal_score ?? null;
  const verdict = payload?.verdict;
  // Phase 44 Batch BF2 — flip deals render ROI / Gross Profit / ARV
  // ratio (not Cap / CoC / CF, which are None on flips). For the
  // target_yield card we re-label as "Max Bid" — the price at which
  // gross margin === 20% (a flip-investor's standard threshold).
  const capPct = payload?.cap_rate != null
    ? `${(payload.cap_rate * 100).toFixed(2)}%`
    : "—";
  const cashFlow = payload?.cash_flow_monthly != null
    ? fmtMoney(payload.cash_flow_monthly) + "/mo"
    : "—";
  const cocPct = payload?.cash_on_cash_pct != null
    ? `${payload.cash_on_cash_pct.toFixed(2)}%`
    : "—";
  // Flip KPIs from compute_flip's scenario output (annualized_roi_pct,
  // gross_profit, arv_ratio). `arv_ratio` is decimal 0..1 (e.g. 0.65).
  const flipRoi = payload?.annualized_roi_pct != null
    ? `${Number(payload.annualized_roi_pct).toFixed(1)}%`
    : "—";
  const flipGrossProfit = payload?.gross_profit != null
    ? fmtMoney(payload.gross_profit)
    : "—";
  const flipArvRatio = payload?.deal_to_arv_pct != null
    ? `${Number(payload.deal_to_arv_pct).toFixed(1)}%`
    : "—";
  // Phase 47 Batch 47B — BRRRR Tri-View KPIs (Capital Recycled,
  // Equity Created, Cash Left in Deal). These come straight off the
  // compute_brrrr output and are present on every BRRRR scenario
  // payload because the BRRRR engine returns them for every basis.
  const brrrrCapitalRecycled = payload?.capital_recycled != null
    ? fmtMoney(payload.capital_recycled)
    : "—";
  const brrrrEquityCreated = payload?.equity_created != null
    ? fmtMoney(payload.equity_created)
    : "—";
  const brrrrCashLeft = payload?.cash_left_in_deal != null
    ? fmtMoney(payload.cash_left_in_deal)
    : "—";

  const ringCls = isActive
    ? "border-indigo-300 ring-2 ring-indigo-200"
    : "border-slate-200";
  const accentBg = {
    slate: "bg-slate-50",
    amber: "bg-amber-50",
    indigo: "bg-indigo-50",
  }[def.accent] || "bg-slate-50";
  const accentIcon = {
    slate: "bg-slate-600 text-white",
    amber: "bg-amber-600 text-white",
    indigo: "bg-indigo-600 text-white",
  }[def.accent] || "bg-slate-600 text-white";

  // Target-yield card is informational only — wrap label with a tooltip.
  // Phase 44 Batch BF2 — on flips, re-label as "Max Bid" since the
  // solved price is the maximum bid that still hits 20% gross margin.
  const targetYieldLabel = isFlip ? "Max Bid" : def.label;
  const labelNode = def.id === "target_yield" ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={`score-scenario-${def.id}-info`}
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-900"
        >
          {targetYieldLabel}
          <Info className="h-3 w-3 text-slate-400" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-left text-xs leading-snug">
        {isFlip ? (
          <>
            <p className="font-semibold">Max Bid Solver</p>
            <p className="mt-1">
              The lower of two anchors — the price below which you
              still walk away with a profit:
            </p>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              <li>
                <span className="font-semibold">70% rule:</span>{" "}
                <span className="tabular-nums">
                  {payload?.solver?.rule70_price != null
                    ? fmtMoney(payload.solver.rule70_price)
                    : "—"}
                </span>
              </li>
              <li>
                <span className="font-semibold">Target margin ({((payload?.solver?.target_margin_pct ?? 0.2) * 100).toFixed(0)}%):</span>{" "}
                <span className="tabular-nums">
                  {payload?.solver?.target_margin_price != null
                    ? fmtMoney(payload.solver.target_margin_price)
                    : "—"}
                </span>
              </li>
            </ul>
            <p className="mt-1.5 italic text-slate-500">
              Treat as a walk-away anchor — never pay above this number.
            </p>
          </>
        ) : (
          <>
            This is the price you&apos;d need to hit your {targetPct}% target yield.
            Use it as a walk-away anchor in negotiations.
          </>
        )}
      </TooltipContent>
    </Tooltip>
  ) : (
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
      {def.label}
    </div>
  );

  return (
    <article
      data-testid={`score-scenario-${def.id}`}
      data-active={isActive ? "true" : "false"}
      aria-pressed={def.clickable ? isActive : undefined}
      className={`relative flex flex-col rounded-2xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5 ${ringCls}`}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg ${accentIcon}`}>
            <Icon className="h-3.5 w-3.5" />
          </span>
          {labelNode}
        </div>
        {isActive ? (
          <span
            data-testid={`score-scenario-${def.id}-active-chip`}
            className="inline-flex items-center gap-0.5 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
          >
            <Check className="h-3 w-3" /> Active
          </span>
        ) : payload?.source === "ai_suggested" ? (
          /* Phase 36 Batch AL — when the counter-offer is the AI's
             suggestion (no user_target_offer set yet), show an
             "AI SUGGESTED" chip so the user knows where the number
             came from. Slate-tone so it doesn't fight with the Active
             indigo chip on the asking card. */
          <span
            data-testid={`score-scenario-${def.id}-ai-chip`}
            className="inline-flex items-center gap-0.5 rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
          >
            <Sparkles className="h-3 w-3" /> AI Suggested
          </span>
        ) : null}
      </header>

      {payload ? (
        <>
          <div className={`mt-3 flex items-center justify-between gap-3 rounded-xl ${accentBg} p-3`}>
            <MiniGauge score={score} size={72} />
            <div className="min-w-0 text-right">
              <div
                data-testid={`score-scenario-${def.id}-price`}
                className="text-base font-bold tabular-nums text-slate-900"
              >
                {fmtMoney(payload.price)}
              </div>
              {/* Phase 47.2 — PSF/PSM subtitle beneath the headline
                  price. Falls back to nothing on bad-sqft so a noisy
                  comp without footage never half-renders. */}
              {sqft && payload.price ? (
                <div
                  data-testid={`score-scenario-${def.id}-psf-psm`}
                  className="mt-0.5 text-[10px] tabular-nums text-slate-500"
                >
                  {formatPsfPsm(payload.price, sqft)}
                </div>
              ) : null}
              {verdict && (
                <span
                  data-testid={`score-scenario-${def.id}-verdict`}
                  className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${verdictPill(verdict, score)}`}
                >
                  {verdict}
                </span>
              )}
            </div>
          </div>

          <dl className="mt-3 grid grid-cols-3 gap-1 text-center text-[11px]" data-testid={`score-scenario-${def.id}-kpis`}>
            {isBrrrr ? (
              <>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">Capital Recycled</dt>
                  <dd
                    data-testid={`score-scenario-${def.id}-capital-recycled`}
                    className="mt-0.5 font-medium tabular-nums text-slate-800"
                  >
                    {brrrrCapitalRecycled}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">Equity Created</dt>
                  <dd
                    data-testid={`score-scenario-${def.id}-equity-created`}
                    className="mt-0.5 font-medium tabular-nums text-slate-800"
                  >
                    {brrrrEquityCreated}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">Cash Left</dt>
                  <dd
                    data-testid={`score-scenario-${def.id}-cash-left`}
                    className={`mt-0.5 font-medium tabular-nums ${
                      payload?.cash_left_in_deal === 0
                        ? "text-emerald-700"
                        : "text-slate-800"
                    }`}
                  >
                    {brrrrCashLeft}
                  </dd>
                </div>
              </>
            ) : isFlip ? (
              <>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">ROI</dt>
                  <dd
                    data-testid={`score-scenario-${def.id}-roi`}
                    className="mt-0.5 font-medium tabular-nums text-slate-800"
                  >
                    {flipRoi}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">G. Profit</dt>
                  <dd
                    data-testid={`score-scenario-${def.id}-gross-profit`}
                    className="mt-0.5 font-medium tabular-nums text-slate-800"
                  >
                    {flipGrossProfit}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">ARV Ratio</dt>
                  <dd
                    data-testid={`score-scenario-${def.id}-arv-ratio`}
                    className="mt-0.5 font-medium tabular-nums text-slate-800"
                  >
                    {flipArvRatio}
                  </dd>
                </div>
              </>
            ) : (
              <>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">Cap</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-slate-800">{capPct}</dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">CoC</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-slate-800">{cocPct}</dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wider text-slate-400">CF</dt>
                  <dd className="mt-0.5 font-medium tabular-nums text-slate-800">{cashFlow}</dd>
                </div>
              </>
            )}
          </dl>

          {def.clickable && !isActive && (
            <button
              type="button"
              onClick={onApply}
              disabled={applying}
              data-testid={`score-scenario-${def.id}-apply`}
              className="mt-3 inline-flex items-center justify-center gap-1 self-end rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            >
              {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {payload?.source === "ai_suggested"
                ? "Use this offer"
                : "Use this scenario"}
            </button>
          )}

          {/* Phase 36 Batch AL — caption explaining WHY the AI picked
              this number (e.g. "Hits your 8.0% cash-on-cash target").
              Only renders for AI-suggested counters; user-typed offers
              don't need a reason. */}
          {payload?.source === "ai_suggested" && payload?.suggested_reason && (
            <p
              data-testid={`score-scenario-${def.id}-ai-reason`}
              className="mt-2 text-[11px] italic leading-snug text-slate-500"
            >
              {payload.suggested_reason}
            </p>
          )}

          {!def.clickable && (
            <p
              data-testid={`score-scenario-${def.id}-walkaway`}
              className="mt-3 text-[11px] italic text-slate-500"
            >
              Walk-away anchor — informational only.
            </p>
          )}
        </>
      ) : (
        <div className="mt-3 flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
          <p className="text-xs text-slate-500">{def.emptyHint}</p>
          {def.id === "counter_offer" && (
            <a
              href="#negotiation"
              data-testid="score-scenario-counter-offer-cta"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline"
              onClick={(e) => {
                // Soft hash navigation: click the Negotiation tab trigger
                // if it's present so we don't leave the deal page. The
                // hash also stays so deep-linking works on direct loads.
                const trigger = document.querySelector('[data-testid="tab-negotiation"]');
                if (trigger) {
                  e.preventDefault();
                  trigger.click();
                }
              }}
            >
              Open Negotiation tab <ArrowRight className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </article>
  );
}
